import {
  Button,
  FormControl,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { User } from '@/_models/User';
import { BottomBar } from '../../ui/BottomBar';
import { Select } from '@/components/common/SelectField';
import { BazanFieldKey, BazanNoteType } from '../../../enums/bazan';
import { AddNoteMode, NoteFormat, NoteIntegration } from '../../../enums';
import { Field, FieldLabel } from '@/components/ui/field';
import { FieldsGroup } from '../../ui/FieldsGroup';
import { useNotesContext } from '../../../hooks/useNotesContext';
import { useEditPosition } from '../../../hooks/useEditPosition';
import { SAPField, SAPNoteForm } from './SAPNoteForm';
import PublicAPI from '@/api/TreedisPublicDAO';
import { HandleAddArgs } from '../../note';
import { FAIcon } from '@packages/ui-components';
import { DescriptionField } from '../components/NoteDescriptionField';

const maxFilesSize = 20971520;
export const BazanForm = ({
  files,
  setFiles,
  handleAdd,
  isNewThread,
  foundUser,
  isMainMessage,
  noteTitle,
  currentMessage,
  onCloseDrawer,
}: {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  handleAdd: (arg: HandleAddArgs) => Promise<string>;
  isNewThread: boolean;
  foundUser?: User;
  isMainMessage?: boolean;
  noteTitle?: string;
  currentMessage?: any;
  onCloseDrawer: () => void;
}) => {
  const {
    addingWorkflow,
    userId,
    mediaUrls,
    setMediaUrls,
    setNoteToEdit,
    noteToEdit,
    integration,
  } = useNotesContext();
  const { handleCancleEditPosition, handleEditPosition, handleSavePosition } = useEditPosition();
  const { tour } = useNotesContext();

  const [description, setDescription] = useState(currentMessage?.description || '');
  const [title, setTitle] = useState(noteTitle || '');
  const [focused, setFocused] = useState(false);
  const [previewSrcs, setPreviewSrcs] = useState<
    { name?: string; lastModified?: number; src?: string }[]
  >([]);
  const [audioURLs, setAudioURLs] = useState<{ lastModified?: number; src?: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [SAPOptions, setSAPOptions] = useState<SAPField[]>([]);
  const [descriptionLength, setDescriptionLength] = useState<number>(0);
  const [loadingFiles, setLoadingFiles] = useState([]);
  const [messageId, setMessageId] = useState<number | null>(currentMessage?.id || null);
  const [noteFormat, setNoteFormat] = useState<NoteFormat>(NoteFormat.General);
  const [SAPData, setSAPData] = useState<Record<string, any>>({});

  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const inputRef = useRef(null);
  const taggingEl = useRef(null);

  const clearInput = () => {
    setDescription('');
    setTitle('');
    setNoteFormat(NoteFormat.General);
    setFocused(false);
    setFiles([]);
    setPreviewSrcs([]);
    setAudioURLs([]);
    setIsLoading(false);
    setMessageId(null);
    // setHasShownError(false);
    setSAPData?.({});
    setAddButtonDisabled(true);
    setNoteToEdit(null);
  };

  const handleChangeTitle = (e) => {
    const { value } = e.target;
    if (value) {
      setTitleError('');
    }
    setTitle(value);
  };

  const handleClickOutside = (event) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target) &&
      taggingEl.current &&
      !taggingEl.current.contains(event.target)
    ) {
      setFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSubmitDisabled = () => {
    if (title === '' && isNewThread) {
      setTitleError('This field cannot be empty');
      return true;
    }

    if (description === '' && !isNewThread) {
      setTitleError('This field cannot be empty');
      return true;
    }

    setDescriptionError('');
    return false;
  };

  useEffect(() => {
    setAddButtonDisabled(isSubmitDisabled());
  }, [title, descriptionLength, mediaUrls, previewSrcs, audioURLs]);

  useEffect(() => {
    if (!noteToEdit?.note?.tagId && isMainMessage) {
      handleEditPosition(noteToEdit.note);
    }
  }, [noteToEdit]);

  const handleFileChange = (event) => {
    const uploadedFiles: File[] = Array.from(event.target.files);

    if (uploadedFiles.length) {
      const fileReaders = uploadedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const { type } = file;

          setLoadingFiles((prevLoading) => [
            ...prevLoading,
            { name: file.name, lastModified: file.lastModified },
          ]);

          if (type === 'image/png' || type === 'image/jpeg' || type === 'image/webp') {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = (e) => {
              resolve({
                name: file.name,
                lastModified: file.lastModified,
                src: e.target.result,
                type: 'image',
              });
            };

            reader.onerror = (e) => {
              reject(e);
            };
          } else if (type.startsWith('video/')) {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'video',
            });
          } else if (type === 'application/pdf') {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'pdf',
            });
          } else {
            toast.error('Unsupported file format');
            reject(new Error('Unsupported file format'));
          }
        });
      });

      Promise.allSettled(fileReaders).then((results) => {
        const successfulResults = results
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<{
              name: string;
              lastModified: number;
              src: string;
            }> => result.status === 'fulfilled',
          )
          .map((result) => result.value);

        const successfulFiles = uploadedFiles.filter(
          (_, index) => results[index].status === 'fulfilled',
        );

        setLoadingFiles([]);

        if (successfulResults.length) {
          setPreviewSrcs((prevSrcs) => [...prevSrcs, ...successfulResults]);
          setFiles((prevUploads) => [...prevUploads, ...successfulFiles]);
        }
      });

      event.target.value = null;
    }
  };

  const removeReview = ({ name, lastModified }: { name?: string; lastModified?: number }) => {
    const filteredPreviews = previewSrcs.filter(
      (preview) => preview.name !== name && preview.lastModified !== lastModified,
    );
    const filteredFiles = files.filter(
      (file) => file.name !== name && file.lastModified !== lastModified,
    );

    setPreviewSrcs(filteredPreviews);
    setFiles(filteredFiles);
  };

  const removeAudio = ({ lastModified }: { lastModified: number }) => {
    const filteredAudios = audioURLs.filter((audioUrl) => audioUrl.lastModified !== lastModified);
    const filteredFiles = files.filter((file) => file.lastModified !== lastModified);

    setAudioURLs(filteredAudios);
    setFiles(filteredFiles);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearInput();
    if (!noteToEdit?.note?.tagId && isMainMessage) {
      handleCancleEditPosition(noteToEdit.note);
    }

    if (isNewThread) {
      onCloseDrawer();
    }
  };

  const handleSave = async () => {
    setAddButtonDisabled(true);
    setIsLoading(true);
    if (isMainMessage && !noteToEdit?.note?.tagId) {
      await handleSavePosition({ ...noteToEdit?.note });
    }
    const result = await handleAdd({
      title,
      description,
      messageId,
      isMainMessage,
      format: noteFormat,
      SAPData,
    });

    if (result === 'Success') {
      setFocused(false);
      clearInput();
    } else {
      toast.error('Could not send message');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const uploadedFilesSize: number = files.reduce((acc, file) => acc + file.size, 0);

    if (files.length + mediaUrls?.length > 10) {
      setAddButtonDisabled(true);
      toast.error('Too many files, max count is 10');
    } else if (uploadedFilesSize > maxFilesSize) {
      setAddButtonDisabled(true);
      toast.error('Files are too large, max is 20mb');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    const fetchSAPOptions = async () => {
      setIsLoading(true);
      try {
        const result = await PublicAPI.getSAPOptions(tour.id);
        if (result?.message === 'Success') {
          setSAPOptions(result.SAPFieldsOptions);
        } else {
          setSAPOptions([]);
        }
      } catch (error) {
        console.error('Error fetching SAP options:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (integration === NoteIntegration.Bazan && addingWorkflow?.active) {
      fetchSAPOptions();
    }
  }, [integration, tour.id, addingWorkflow]);

  const faultSourceField = SAPOptions.find((o) => o.key === BazanFieldKey.FaultSource);
  const noteTypeField = SAPOptions.find((o) => o.key === BazanFieldKey.NoteType);

  useEffect(() => {
    if (SAPData?.[BazanFieldKey.NoteType] || !noteTypeField) return;
    const intialNoteType = noteTypeField.options[0].value;
    setSAPData?.((prev) => ({
      ...prev,
      [BazanFieldKey.NoteType]: intialNoteType,
    }));
  }, [noteTypeField, SAPData, setSAPData]);

  const shouldShowSAPField = (fieldKey: BazanFieldKey, format?: NoteFormat) => {
    const field = SAPOptions.find((o) => o.key === fieldKey);
    const isFormatOk = format ? noteFormat === format : true;
    return (
      integration === NoteIntegration.Bazan &&
      isNewThread &&
      addingWorkflow?.mode !== AddNoteMode.Tag &&
      !!field &&
      isFormatOk
    );
  };

  return (
    <>
      <FormControl sx={{ height: '100%', p: '18px' }}>
        <Stack direction="column" gap="10px" height="100%">
          {shouldShowSAPField(BazanFieldKey.NoteType) && (
            <Field>
              <FieldLabel>Note type</FieldLabel>
              <Select
                value={SAPData?.[BazanFieldKey.NoteType] ?? ''}
                onChange={(v) => {
                  console.log(v);
                  setNoteFormat(
                    v.value === BazanNoteType.General ? NoteFormat.General : NoteFormat.Bazan,
                  );

                  setSAPData?.((prevData) => ({
                    ...prevData,
                    [BazanFieldKey.NoteType]: v.value,
                  }));
                }}
                options={noteTypeField.options}
              />
            </Field>
          )}

          <FieldsGroup
            label="Properties"
            tooltip="Properties"
            passThrough={!!noteToEdit || !isNewThread}
            defaultExpanded
          >
            {shouldShowSAPField(BazanFieldKey.NoteType, NoteFormat.Bazan) && (
              <Field>
                <FieldLabel>Fault Source</FieldLabel>
                <Select
                  value={SAPData?.[BazanFieldKey.FaultSource] ?? ''}
                  options={
                    faultSourceField.options instanceof Array ? faultSourceField.options : []
                  }
                  onChange={({ value }) => {
                    setSAPData?.((prevData) => ({
                      ...prevData,
                      [BazanFieldKey.FaultSource]: value,
                    }));
                  }}
                />
              </Field>
            )}

            {(isNewThread || (isMainMessage && !!noteToEdit)) && (
              <Field>
                <FieldLabel>Title</FieldLabel>
                <TextField
                  variant="outlined"
                  {...(noteToEdit ? { defaultValue: title } : { value: title })}
                  placeholder="Write a title"
                  onChange={handleChangeTitle}
                  inputProps={{
                    style: {
                      fontFamily: 'inherit',
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 400,
                      padding: '10px 14px',
                      height: '45px',
                    },
                  }}
                  InputProps={{
                    endAdornment: titleError ? (
                      <InputAdornment
                        position="end"
                        style={{ position: 'absolute', right: '14px', bottom: '50%' }}
                      >
                        <FAIcon icon="faCircleExclamation" color="red" size={16} />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: '100%',
                    borderRadius: '8px',
                    border: titleError ? 'none' : '1px solid #D0D5DD',
                    margin: 0,
                    background: titleError ? 'transparent' : '#FFF',
                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                    '& fieldset': { height: '100%', top: 0 },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D0D5DD',
                      borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      flexDirection: 'column',
                      '&.Mui-error': {
                        borderColor: 'red',
                        background: '#FFF',
                      },
                    },
                    fontWeight: 400,
                  }}
                  InputLabelProps={{
                    style: {
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#667085',
                      opacity: 1,
                    },
                  }}
                  error={!!titleError}
                  helperText={titleError}
                />
              </Field>
            )}

            <Field>
              {(isNewThread || !!noteToEdit) && (
                <FieldLabel tooltip="tooltip">Description</FieldLabel>
              )}
              <DescriptionField
                focused={focused}
                setFocused={setFocused}
                foundUser={foundUser}
                isNewThread={isNewThread}
                editMode={!!noteToEdit}
                setFiles={setFiles}
                addButtonDisabled={addButtonDisabled}
                setAddButtonDisabled={setAddButtonDisabled}
                setIsLoading={setIsLoading}
                handleAdd={handleAdd}
                clearInput={clearInput}
                description={description}
                setDescription={setDescription}
                setAudioURLs={setAudioURLs}
                previewSrcs={previewSrcs}
                loadingFiles={loadingFiles}
                audioURLs={audioURLs}
                mediaUrls={mediaUrls}
                isLoading={isLoading}
                removeReview={removeReview}
                removeAudio={removeAudio}
                setMediaUrls={setMediaUrls}
                handleFileChange={handleFileChange}
                descriptionLength={descriptionLength}
                setDescriptionLength={setDescriptionLength}
                descriptionError={descriptionError}
              />
            </Field>
          </FieldsGroup>

          {noteFormat === NoteFormat.Bazan && (
            <>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <SAPNoteForm SAPData={SAPData} setSAPData={setSAPData} SAPFieldsData={SAPOptions} />
              )}
            </>
          )}
        </Stack>
      </FormControl>

      {isNewThread && (
        <BottomBar justifyContent="flex-end" p="10px">
          <Button
            variant="outlined"
            size="small"
            sx={{
              border: '1px solid #D0D5DD',
              borderRadius: '8px',
              paddingX: '18px',
              paddingY: '10px',
              color: '#101828',
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '24px',
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={addButtonDisabled}
            variant="contained"
            color="primary"
            size="small"
            sx={{
              background: '#2055FF',
              color: 'white',
              font: 'Inter',
              fontWeight: '500',
              fontSize: '16px',
              paddingX: '18px',
              paddingY: '10px',
              borderRadius: '8px',
              lineHeight: '24px',
            }}
            onClick={handleSave}
          >
            Create
          </Button>
        </BottomBar>
      )}
    </>
  );
};
